import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, CheckCircle, Activity, Lightbulb } from "lucide-react";
import { cn } from "@/lib/utils";

interface QuickActionToolbarProps {
  onSmartRevision: () => void;
  onContentHealth: () => void;
  onQuickFormat: () => void;
  onGetSuggestions: () => void;
  isLoading?: boolean;
}

export const QuickActionToolbar = ({
  onSmartRevision,
  onContentHealth,
  onQuickFormat,
  onGetSuggestions,
  isLoading = false,
}: QuickActionToolbarProps) => {
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Make sticky when scrolled down 200px
      setIsSticky(window.scrollY > 200);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div
      className={cn(
        "transition-all duration-300 z-50",
        isSticky
          ? "fixed top-20 right-6 shadow-lg"
          : "relative"
      )}
    >
      <div className="bg-background border rounded-lg p-2 flex flex-col gap-2 backdrop-blur-sm bg-opacity-95">
        <Button
          onClick={onSmartRevision}
          disabled={isLoading}
          size="sm"
          variant="default"
          className="flex items-center gap-2 justify-start"
          title="Comprehensive AI revision"
        >
          <Sparkles className="h-4 w-4" />
          <span className={cn(isSticky && "hidden xl:inline")}>Smart Revision</span>
        </Button>

        <Button
          onClick={onContentHealth}
          disabled={isLoading}
          size="sm"
          variant="outline"
          className="flex items-center gap-2 justify-start"
          title="Check content quality"
        >
          <Activity className="h-4 w-4" />
          <span className={cn(isSticky && "hidden xl:inline")}>Check Quality</span>
        </Button>

        <Button
          onClick={onQuickFormat}
          disabled={isLoading}
          size="sm"
          variant="outline"
          className="flex items-center gap-2 justify-start"
          title="Auto-format content"
        >
          <CheckCircle className="h-4 w-4" />
          <span className={cn(isSticky && "hidden xl:inline")}>Quick Format</span>
        </Button>

        <Button
          onClick={onGetSuggestions}
          disabled={isLoading}
          size="sm"
          variant="outline"
          className="flex items-center gap-2 justify-start"
          title="Get AI suggestions"
        >
          <Lightbulb className="h-4 w-4" />
          <span className={cn(isSticky && "hidden xl:inline")}>Suggestions</span>
        </Button>
      </div>
    </div>
  );
};
